import { apiService } from './apiService';

export interface Sponsor {
  id: string;
  name: string;
  photo_url: string | null;
  bio: string;
  role: string;
  interests: string[];
  whatsapp_number: string;
  crp?: string | null;
  telemed?: SponsorTelemed | null;
  matchScore?: number;
  sharedInterests?: string[];
  created_at?: string;
}

export interface SponsorTelemed {
  enabled: boolean;
  appointment_type?: string | null;
  duration_minutes?: number | null;
  amount_minor?: number | null;
  currency?: string | null;
  timezone?: string | null;
  availability_pattern?: Record<string, string[]> | null;
  updated_at?: string | null;
}

export interface AvailableSlot {
  date: string;
  time: string;
  weekday: string;
  available: boolean;
}

export interface SponsorAgendaSummary {
  id: string;
  name: string;
  photo_url: string | null;
  role?: string | null;
  crp?: string | null;
  telemed?: Pick<
    SponsorTelemed,
    'enabled' | 'duration_minutes' | 'amount_minor' | 'currency'
  > | null;
  sessionDurationMinutes: number;
}

export interface SponsorAppointment {
  id: string;
  sponsor_id: string;
  user_id: string;
  scheduled_date: string;
  scheduled_time: string;
  status: string;
  user_phone?: string;
  // Telemedicine Phase 1 · P1.SRV.4 — populated for newly booked appointments.
  // Legacy rows booked before the migration may have these as null/undefined,
  // in which case the join button is hidden until lazy allocation runs on the
  // server's next join-token call.
  scheduled_at_utc?: string | null;
  video_channel_id?: string | null;
  sponsors?: {
    id: string;
    name: string;
    photo_url: string | null;
    whatsapp_number: string;
    role: string;
  };
}

export interface AppointmentBookingQuote {
  sponsorId: string;
  scheduledDate: string;
  scheduledTime: string;
  amountMinor: number;
  currency: string;
  durationMinutes: number;
}

export interface AppointmentBookingSession {
  id: string;
  status:
    | 'pending'
    | 'paid'
    | 'finalized'
    | 'failed'
    | 'expired'
    | 'canceled'
    | 'requires_refund'
    | 'refunded';
  appointmentId?: string | null;
}

/**
 * Telemedicine Phase 1 · P1.SRV.6
 * Response shape for the on-demand join-token endpoint.
 */
export interface AppointmentJoinToken {
  channel: string;
  token: string;
  joinUrl: string;
  scheduledAtUtc: string | null;
}

export interface RecoverableAppointmentCheckout {
  id: string;
  status: 'pending';
  expiresAt: string;
  scheduledDate: string;
  scheduledTime: string;
  amountMinor: number;
  currency: string;
  durationMinutes: number | null;
  sponsor: {
    id: string;
    name: string | null;
    photoUrl: string | null;
    role: string | null;
  };
}

/**
 * Get 2 sponsor matches based on user's interests
 * CRITICAL FIX: Uses apiService which has the auth token set synchronously
 * Plus status is verified server-side via RevenueCat REST API - no client trust
 */
export async function getMatchedSponsors(): Promise<Sponsor[]> {
  try {
    console.log('👥 SponsorService: Loading matched sponsors via apiService...');
    const response = await apiService.get<{ sponsors: Sponsor[] }>('/api/sponsors/match');

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch sponsor matches');
    }

    return response.data?.sponsors || [];
  } catch (error) {
    console.error('Error fetching matched sponsors:', error);
    throw error;
  }
}

/**
 * Get 14-day availability for a specific sponsor
 * Returns ALL time slots (10:00-17:00) with availability status
 */
export async function getSponsorAvailability(sponsorId: string): Promise<{
  sponsor: SponsorAgendaSummary;
  slots: AvailableSlot[];
}> {
  try {
    console.log('📅 SponsorService: Loading sponsor availability via apiService...');
    const response = await apiService.get<{
      sponsor: SponsorAgendaSummary;
      slots: AvailableSlot[];
    }>(`/api/sponsors/${sponsorId}/availability`);

    if (!response.success) {
      throw new Error(response.error || 'Failed to fetch sponsor availability');
    }

    return response.data!;
  } catch (error) {
    console.error('Error fetching sponsor availability:', error);
    throw error;
  }
}

/**
 * Schedule an appointment with a sponsor
 */
export async function scheduleSponsorAppointment(params: {
  sponsorId: string;
  scheduledDate: string;
  scheduledTime: string;
  userPhone?: string;
}): Promise<SponsorAppointment> {
  try {
    // Get device timezone for accurate notification scheduling
    const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
    console.log('🌍 Scheduling appointment with timezone:', userTimezone);
    console.log('📅 SponsorService: Scheduling appointment via apiService...');

    const response = await apiService.post<{ appointment: SponsorAppointment }>(
      '/api/sponsors/schedule',
      {
        ...params,
        userTimezone,
      }
    );

    if (!response.success) {
      throw new Error(response.error || 'Failed to schedule appointment');
    }

    return response.data!.appointment;
  } catch (error) {
    console.error('Error scheduling appointment:', error);
    throw error;
  }
}

export async function getAppointmentBookingQuote(params: {
  sponsorId: string;
  scheduledDate: string;
  scheduledTime: string;
}): Promise<AppointmentBookingQuote> {
  const response = await apiService.post<{ quote: AppointmentBookingQuote }>(
    '/api/sponsors/booking-quote',
    params
  );
  if (!response.success || !response.data?.quote) {
    throw new Error(response.error || 'Unable to get appointment quote');
  }
  return response.data.quote;
}

export async function createAppointmentBookingSession(params: {
  sponsorId: string;
  scheduledDate: string;
  scheduledTime: string;
  userTimezone?: string;
}): Promise<{ session: AppointmentBookingSession; paymentIntentClientSecret: string }> {
  const response = await apiService.post<{
    session: AppointmentBookingSession;
    paymentIntentClientSecret: string;
  }>('/api/sponsors/booking-sessions', params);
  if (!response.success || !response.data?.session || !response.data.paymentIntentClientSecret) {
    throw new Error(response.error || 'Unable to start appointment checkout');
  }
  return response.data;
}

export async function getStripePublishableKey(): Promise<string> {
  const response = await apiService.get<{ publishableKey: string }>('/api/stripe/config');
  if (!response.success || !response.data?.publishableKey) {
    throw new Error(response.error || 'Payment service is unavailable');
  }
  return response.data.publishableKey;
}

export async function cancelAppointmentBookingSession(sessionId: string): Promise<void> {
  const response = await apiService.post(`/api/sponsors/booking-sessions/${sessionId}/cancel`, {});
  if (!response.success) throw new Error(response.error || 'Unable to cancel appointment checkout');
}

export async function getRecoverableAppointmentCheckouts(): Promise<
  RecoverableAppointmentCheckout[]
> {
  const response = await apiService.get<{ sessions: RecoverableAppointmentCheckout[] }>(
    '/api/sponsors/booking-sessions/recoverable'
  );
  if (!response.success) {
    throw new Error(response.error || 'Unable to load pending appointment payments');
  }
  return response.data?.sessions || [];
}

export async function resumeAppointmentBookingSession(
  sessionId: string
): Promise<{ session: AppointmentBookingSession; paymentIntentClientSecret: string }> {
  const response = await apiService.post<{
    session: AppointmentBookingSession;
    paymentIntentClientSecret: string;
  }>(`/api/sponsors/booking-sessions/${encodeURIComponent(sessionId)}/resume`, {});
  if (!response.success || !response.data?.session || !response.data.paymentIntentClientSecret) {
    throw new Error(response.error || 'Unable to resume appointment checkout');
  }
  return response.data;
}

export async function getAppointmentBookingSession(sessionId: string): Promise<AppointmentBookingSession> {
  const response = await apiService.get<{ session: AppointmentBookingSession }>(
    `/api/sponsors/booking-sessions/${sessionId}`
  );
  if (!response.success || !response.data?.session) {
    throw new Error(response.error || 'Unable to check appointment payment status');
  }
  return response.data.session;
}

/**
 * Get user's scheduled appointments
 * CRITICAL FIX: Uses apiService which has the auth token set synchronously
 */
export async function getMyAppointments(): Promise<SponsorAppointment[]> {
  try {
    console.log('📡 SponsorService: Fetching appointments via apiService...');

    const response = await apiService.get<{ appointments: SponsorAppointment[] }>(
      '/api/sponsors/my-appointments'
    );

    console.log('📡 Response status:', response.status, response.success ? 'OK' : 'FAILED');

    if (!response.success) {
      console.error('❌ Response error:', response.error);
      throw new Error(response.error || 'Failed to fetch appointments');
    }

    console.log('📋 API response:', response.data);
    return response.data?.appointments || [];
  } catch (error) {
    console.error('❌ Error fetching appointments:', error);
    throw error;
  }
}

/**
 * Telemedicine Phase 1 · P1.SRV.6
 *
 * Mint a fresh patient link token for an in-app join. Server enforces that
 * the appointment belongs to the caller and is in an active status. The
 * returned token has a flat 3h TTL — caller does NOT need to cache it.
 */
export async function getAppointmentJoinToken(
  appointmentId: string
): Promise<AppointmentJoinToken> {
  const response = await apiService.post<AppointmentJoinToken>(
    `/api/sponsors/appointments/${appointmentId}/join-token`,
    {}
  );
  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to mint join token');
  }
  return response.data;
}

/**
 * Reschedule an appointment to a new date/time.
 * The server atomically cancels the old appointment and creates a new one.
 */
export async function rescheduleAppointment(params: {
  appointmentId: string;
  newDate: string;
  newTime: string;
}): Promise<SponsorAppointment> {
  const userTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Sao_Paulo';
  const response = await apiService.post<{ appointment: SponsorAppointment }>(
    '/api/sponsors/reschedule-appointment',
    { ...params, userTimezone }
  );
  if (!response.success || !response.data) {
    const error = Object.assign(
      new Error(response.error || 'Failed to reschedule appointment'),
      { code: response.code, status: response.status }
    );
    throw error;
  }
  return response.data.appointment;
}

/**
 * Get user's appointment history (past/completed/canceled sessions)
 */
export async function getMyPastAppointments(): Promise<SponsorAppointment[]> {
  try {
    console.log('📡 SponsorService: Fetching appointment history via apiService...');

    const response = await apiService.get<{ appointments: SponsorAppointment[] }>(
      '/api/sponsors/my-appointments/history'
    );

    console.log('📡 Response status:', response.status, response.success ? 'OK' : 'FAILED');

    if (!response.success) {
      console.error('❌ Response error:', response.error);
      throw new Error(response.error || 'Failed to fetch appointment history');
    }

    console.log('📋 API response:', response.data);
    return response.data?.appointments || [];
  } catch (error) {
    console.error('❌ Error fetching appointment history:', error);
    throw error;
  }
}

/**
 * Cancel a sponsor appointment
 */
export async function cancelAppointment(appointmentId: string): Promise<void> {
  try {
    console.log('🗑️ SponsorService: Canceling appointment via apiService...');
    // Server exposes POST /api/sponsors/cancel-appointment (not a REST DELETE).
    // Hitting the wrong verb/path silently swallowed cancellations before P1.SRV.4.
    const response = await apiService.post(`/api/sponsors/cancel-appointment`, { appointmentId });

    if (!response.success) {
      const error = Object.assign(
        new Error(response.error || 'Failed to cancel appointment'),
        { code: response.code, status: response.status }
      );
      throw error;
    }
  } catch (error) {
    console.error('Error canceling appointment:', error);
    throw error;
  }
}

/**
 * Generate WhatsApp deep link for messaging a sponsor
 * @param phoneNumber - WhatsApp phone number (format: +5511999887766)
 * @param message - Optional pre-filled message
 * @returns WhatsApp deep link
 */
export function getWhatsAppLink(phoneNumber: string, message?: string): string {
  // Remove all non-digit characters except +
  const cleanPhone = phoneNumber.replace(/[^\d+]/g, '');

  // Remove + if present, WhatsApp API expects just digits
  const phoneDigits = cleanPhone.replace('+', '');

  if (message) {
    const encodedMessage = encodeURIComponent(message);
    return `whatsapp://send?phone=${phoneDigits}&text=${encodedMessage}`;
  }

  return `whatsapp://send?phone=${phoneDigits}`;
}

/**
 * Format phone number for display (BR format)
 * +5511999887766 -> (11) 99988-7766
 */
export function formatPhoneNumber(phone: string): string {
  const digits = phone.replace(/\D/g, '');

  if (digits.length === 13 && digits.startsWith('55')) {
    // Brazilian number: +55 11 99988-7766
    const ddd = digits.slice(2, 4);
    const firstPart = digits.slice(4, 9);
    const secondPart = digits.slice(9);
    return `(${ddd}) ${firstPart}-${secondPart}`;
  }

  return phone;
}

/**
 * Group available slots by date
 */
export function groupSlotsByDate(slots: AvailableSlot[]): Record<string, AvailableSlot[]> {
  return slots.reduce(
    (acc, slot) => {
      const date = slot.date;
      if (!acc[date]) {
        acc[date] = [];
      }
      acc[date].push(slot);
      return acc;
    },
    {} as Record<string, AvailableSlot[]>
  );
}
